import { createPortal } from 'react-dom';
import { AgentHost } from './agents/AgentHost';
import { objSrc } from './home/ObjectLibrary';
import React, { useState, useEffect } from 'react';
import { Apres } from './kit/Apres';
import { RechercheGlobale } from './recherche/RechercheGlobale';
import { Decider } from './decider/Decider';
import { CreerSheet, useCreerSheet } from './creer/CreerSheet';

const CreerOrdi = () => { const [open, setOpen] = useCreerSheet(); return <CreerSheet open={open} onClose={() => setOpen(false)} />; };
import { poserNuit, moduleDe } from './ig/nuit28';
import '../styles/nuit28.css';
import '../styles/nuit-theme.css';
import '../styles/lot41.css';
import { useIsMobile, useMobileViewport } from '../hooks/useMobileLayout';
import { useTitleDots } from '../hooks/useTitleDots';
import { useHappnButtons } from '../hooks/useHappnButtons';
import { useNoIconButtons } from '../hooks/useNoIconButtons';
import { useContextualAgent } from '../hooks/useContextualAgent';
import { openAgentChat } from '../utils/agents';
import { BottomNav } from './BottomNav';
import { BulleExpert } from './BulleExpert';
import {DemoPOVBanner} from './DemoPOVBanner';
import {DemoTour} from './DemoTour';
import {TrialBanner} from './TrialBanner';
import {Onboarding48Banner} from './Onboarding48Banner';
import {MiseEnRouteBanner} from './MiseEnRouteBanner';
import { DesktopSidebar, ModuleClassicBadge, useNavStyle } from './DesktopSidebar';
import { Sidebar360 } from './accueil/Sidebar360';
import { CreditsHost } from './accueil/CreditsSheet';
import { PopupModuleHost, enPopup, usePopupSeul } from './layout/PopupModule';
import { ObjectifsHost } from './ig/Objectifs41';
import { U12Host } from './stocks/ProduitU12';
import { SortirHost } from './ig/Sortir41';
import '../pages/admin360/admin360.css';
import { FamillesListe42 } from './ig/ModuleProfil';
import { LimiteErreur } from './layout/LimiteErreur';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { CreditsPill } from './credits/CreditsPill';
import { AccesParRole } from './equipe/AccesParRole';
import { useSyncMode } from './ig/InterrupteurMode';
import { IgTopBar } from './fil/IgTopBar';
import { Bell, HelpCircle, X, ChevronRight, MessageCircle, RotateCcw, BookOpen, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { pepFor } from '../utils/pep';
import { useOnboardingTour } from './OnboardingTour';
import { PresenceIndicator } from './PresenceIndicator';
import { GreenCardsToaster } from './GreenCardsToaster';
import { ImportCaisseWatcher } from './pos/ImportCaisseWatcher';
import { SwipeBack } from './ios/SwipeBack';
import { KitModal } from './da/Kit';
import { ExpertLettersWidget } from './ExpertLettersWidget';
import { FicheNotifications, chargerNotifsFiches } from './fiche/FicheNotifications';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import { haptic } from '../utils/haptics';
import { useEdgeSwipeBack } from '../hooks/useEdgeSwipeBack';
import { toast } from 'sonner';
import '../styles/home-da.css';
import '../styles/header-tools.css';
import '../styles/mobile-ios.css';
import { moduleNote, moduleObject } from '../utils/moduleDa';
import { EmployeeFiche } from './equipe/EmployeeFiche';

// Bensimon Logo Component for header
import { MissionsPopupHote } from './missions/MissionsOnglet';
import { MarqueImpression } from './kit/MarqueEtablissement';
const BensimonLogoSmall = ({ size = 28, color = '#C46A4A' }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
    <g>
      <path d="M25 15 L25 85" stroke={color} strokeWidth="5" strokeLinecap="round" />
      <path d="M25 15 C25 15, 70 15, 70 32.5 C70 50, 25 50, 25 50" stroke={color} strokeWidth="5" strokeLinecap="round" fill="none" />
      <path d="M25 50 C25 50, 75 50, 75 67.5 C75 85, 25 85, 25 85" stroke={color} strokeWidth="5" strokeLinecap="round" fill="none" />
      <line x1="18" y1="32" x2="62" y2="32" stroke={color} strokeWidth="2" />
      <line x1="18" y1="67" x2="68" y2="67" stroke={color} strokeWidth="2" />
    </g>
  </svg>
);

// Tenant Logo for header
const HeaderTenantLogo = ({ tenant, size = 28 }) => {
  const logoType = tenant?.branding?.logo_type || null;
  const primaryColor = tenant?.branding?.primary_color || 'var(--brand)';
  
  if (logoType === 'svg_b' || tenant?.slug === 'bensimon') {
    return <BensimonLogoSmall size={size} color={primaryColor} />;
  }
  // V5.99.17 — Keep the legacy 🥑 logo only when the tenant has explicitly
  // opted-in via `branding.logo_type === 'avocado'` (currently only Maison
  // Avocado has this set in DB). All other tenants get the neutral 360 brand.
  if (logoType === 'avocado') {
    return <span className="text-2xl"></span>;
  }
  if (tenant?.branding?.logo_url) {
    return <img src={tenant.branding.logo_url} alt={tenant?.name || '360Resto OS'} className="w-9 h-9 object-contain" data-testid="header-tenant-logo" />;
  }
  return (
    <span
      className="w-8 h-8 rounded-lg flex items-center justify-center text-[var(--sur-couleur)] font-extrabold text-sm shadow-sm"
      style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)` }}
      aria-label="Logo"
    >
      360
    </span>
  );
};


// 01.3 · Établissement actif (nom · ville) + sélecteur des établissements accessibles (un seul → nom sans sélecteur)
const HeaderTenant = ({ tenant }) => {
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { axios.get(`${getApiUrl()}/tenant/sites`, { withCredentials: true }).then((r) => setSites(r.data?.sites || [])).catch(() => setSites([])); }, []);
  const city = tenant?.city || tenant?.ville || tenant?.address?.city || (tenant?.country ? new Intl.DisplayNames(['fr'], { type: 'region' }).of(tenant.country) : '');
  const many = sites.length > 1;
  const pick = async (site) => { try { await axios.post(`${getApiUrl()}/tenant/sites/switch`, { site_id: site.site_id }, { withCredentials: true }); window.location.reload(); } catch { toast.error('Changement impossible'); } };
  if (!many) return null; // un seul établissement : le nom est déjà dans la barre latérale
  return <div className="relative hidden sm:block">
    <button type="button" className={`hdr-tenant ${many ? 'is-pick' : ''}`} disabled={!many} aria-haspopup={many ? 'listbox' : undefined} aria-expanded={many ? open : undefined} title={many ? 'Changer d’établissement' : 'Établissement actif'} onClick={() => many && setOpen((v) => !v)} data-testid="header-tenant">
      <span className="hdr-tenant-name" data-testid="header-tenant-name">{tenant?.name || '360 Resto'}</span>
      {city && <span className="hdr-tenant-city" data-testid="header-tenant-city">{city}</span>}
      {many && <ChevronRight size={14} className="rotate-90" />}
    </button>
    {open && many && <ul role="listbox" className="hdr-menu" data-testid="header-tenant-menu">
      {sites.map((st) => <li key={st.site_id}><button type="button" role="option" aria-selected={st.is_active} className="sb-menu-item" onClick={() => pick(st)} data-testid={`header-tenant-pick-${st.site_id}`}>{st.name}{st.city ? ` · ${st.city}` : ''}</button></li>)}
    </ul>}
  </div>;
};

// 01.1 · « Mon compte » : initiales + libellé visible (plus de pastille muette)
const HeaderAccount = ({ user, logout, basePath, navigate }) => {
  const [open, setOpen] = useState(false);
  const ini = (user?.name || user?.email || 'U').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return <div className="relative">
    <button type="button" className="hdr-btn" data-keepicon="1" aria-haspopup="menu" aria-expanded={open} aria-label={`Mon compte · ${user?.name || ''}`} title={`Mon compte · ${user?.name || user?.email || ''}`} onClick={() => setOpen((v) => !v)} data-testid="header-account">
      <span className="hdr-ini" aria-hidden="true">{ini}</span><span className="hdr-lbl">Mon compte</span>
    </button>
    {open && <div role="menu" className="hdr-menu" data-testid="header-account-menu">
      <button type="button" role="menuitem" className="sb-menu-item" onClick={() => { setOpen(false); navigate(`${basePath}/dashboard?feuille=moi`); }} data-testid="header-account-profile">Mon profil</button>
      <button type="button" role="menuitem" className="sb-menu-item" onClick={() => { setOpen(false); navigate(`${basePath}/dashboard?feuille=moi`); }} data-testid="header-account-password">Mot de passe</button>
      <button type="button" role="menuitem" className="sb-menu-item is-danger" onClick={logout} data-testid="header-logout">Déconnexion</button>
    </div>}
  </div>;
};

export const DashboardLayout = ({ children, title, showBack = false, hideTitle = false, hideExperts = false }) => {
  usePopupSeul();
  // F2 · dossier employé unique ouvert depuis n'importe quel nom / avatar
  const [ficheId, setFicheId] = useState(null);
  useEffect(() => { const h = (ev) => setFicheId(ev.detail?.id || null); window.addEventListener('open-employee-fiche', h); return () => window.removeEventListener('open-employee-fiche', h); }, []);
  useMobileViewport();
  useTitleDots();
  useHappnButtons();
  useNoIconButtons();
  const mobile = useIsMobile();
  const contextualAgent = useContextualAgent();
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { pathname } = useLocation();
  const basePath = tenantSlug ? `/${tenantSlug}` : '';
  const accesParRole = <AccesParRole slug={tenantSlug} pathname={pathname} />;
  useSyncMode();
  useEffect(() => { if (tenantSlug) try { localStorage.setItem('r360_last_app', `/${tenantSlug}/dashboard`); } catch { /* stockage indisponible */ } }, [tenantSlug]);
  const goHome = React.useCallback(() => { haptic.tap(); navigate(`${basePath}/dashboard`); }, [navigate, basePath]);
  const swipeRef = useEdgeSwipeBack({ enabled: mobile && showBack, onBack: goHome });
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  React.useEffect(() => {
    let alive = true;
    // La cloche ne compte QUE les notifications non lues de l'utilisateur (jamais un total d'éléments à traiter dans un module).
    const tick = () => Promise.all([
      axios.get(`${getApiUrl()}/ai/experts/letters`, { withCredentials: true }).then((r) => (r.data?.unread ? (r.data.letters || []).length : 0)).catch(() => 0),
      chargerNotifsFiches().then((d) => d.non_lues || 0),
    ]).then(([a, b]) => { if (alive) setNotifCount(a + b); });
    tick(); const id = setInterval(tick, 120000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // 10 · sidebar rétractable (icône 4 points en haut, mémorisée)
  const [sbCollapsed, setSbCollapsed] = useState(() => localStorage.getItem('da_sidebar_collapsed') === '1');
  useEffect(() => { document.documentElement.classList.toggle('sb-replie', sbCollapsed); return () => document.documentElement.classList.remove('sb-replie'); }, [sbCollapsed]);
  const toggleSb = () => setSbCollapsed((v) => { localStorage.setItem('da_sidebar_collapsed', v ? '0' : '1'); return !v; });
  const { resetTour } = useOnboardingTour();

  // Get tenant branding for header
  const tenantName = tenant?.name || '360 Resto';
  const fullTitle = title || tenantName;
  // Mobile : on garde le nom court du module (« HACCP » plutôt que « HACCP — Hygiène & conformité »)
  const displayTitle = mobile && fullTitle ? String(fullTitle).split(/\s+[—–·|]\s+/)[0] : fullTitle;
  const navStyle = useNavStyle();
  // la nuit du module est posée dès le squelette de chargement (plus d'écran blanc avant le profil)
  React.useLayoutEffect(() => poserNuit(moduleDe(title, /\/dashboard$/.test(pathname))), [title, pathname]);

  // Listen for sidebar open events from child components
  React.useEffect(() => {
    const handleOpenSidebar = () => setSidebarOpen(true);
    window.addEventListener('openSidebar', handleOpenSidebar);
    return () => window.removeEventListener('openSidebar', handleOpenSidebar);
  }, []);

  const handleResetTour = () => {
    resetTour();
    setShowHelpMenu(false);
    toast.success('Guide de formation relancé !');
    window.location.reload();
  };

  return (
    <div className={`app-shell min-h-screen bg-[var(--remplissage)]${enPopup() ? ' is-popup41' : ''}`} data-testid="app-shell">
      {/* Background green-cards toast notifier */}
      {/* 01.5 · Notifications = lettres des experts (information) ; la seule liste à traiter reste le Centre d'actions */}
      {notifOpen && <KitModal open onClose={() => setNotifOpen(false)} kicker="Pour information · les choses à traiter sont dans le Centre d'actions" title="Notifications" testId="notifications-modal">
        <ExpertLettersWidget tenantSlug={tenantSlug} onRead={() => setNotifCount(0)} />
        <FicheNotifications tenantSlug={tenantSlug} onRead={(k) => setNotifCount((n) => Math.max(0, n - (k || 0)))} />
        <div className="rim-foot" style={{ marginTop: 12 }}><button type="button" className="fh-btn" onClick={() => { setNotifOpen(false); navigate(`${basePath}/actions`); }} data-testid="notifications-open-actions">Ouvrir le Centre d'actions</button></div>
      </KitModal>}
      <Apres>
      <GreenCardsToaster />
      <ImportCaisseWatcher />
      </Apres>
      {/* Barre de gauche (brief FINAL · écran 30) sur PC · l'ancien tiroir reste pour le téléphone */}
      <Apres si={typeof window !== 'undefined' && window.innerWidth < 768}><Sidebar360 compact={sbCollapsed} /></Apres>
      <Apres>
      <RechercheGlobale />
      <Decider />
      {typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches && <CreerOrdi />}
      <DesktopSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} collapsed mobileOnly />
      <CreditsHost />
      <PopupModuleHost />
      <ObjectifsHost />
      <U12Host />
      <FamillesListe42 />
      </Apres>
      {user?.plateforme_360 && createPortal(<button type="button" className="retour-admin-360" onPointerDown={(e) => { e.stopPropagation(); window.location.assign('/admin-360'); }} onClick={() => window.location.assign('/admin-360')} data-testid="retour-admin-360">← Retour à l’admin</button>, document.body)}
      <SortirHost slug={tenant?.slug || window.location.pathname.split('/')[1]} onCompte={() => { window.location.href = `/${tenant?.slug || window.location.pathname.split('/')[1]}/dashboard?feuille=compte`; }} />
      <button type="button" onClick={toggleSb} className={`sb-toggle hidden md:grid ${sbCollapsed ? 'is-collapsed' : ''}`} aria-label={sbCollapsed ? 'Déplier la barre' : 'Replier la barre'} aria-expanded={!sbCollapsed} title={sbCollapsed ? 'Déplier la barre' : 'Replier la barre'} data-testid="sidebar-toggle">
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">{sbCollapsed ? <path d="M7 4l5 5-5 5" /> : <path d="M11 4L6 9l5 5" />}</svg>
      </button>
      
      {/* Main Content Area - Horaires right on desktop */}
      <div className={`${sbCollapsed ? 'md:ml-[72px] sb-is-collapsed' : 'md:ml-72'} min-h-screen home-da app-da`} data-module={displayTitle} data-pep={pepFor(pathname)} data-sidebar={sbCollapsed ? 'collapsed' : 'open'}>
        <DemoPOVBanner/>
        <TrialBanner/>
        <Onboarding48Banner/>
        <MiseEnRouteBanner/>
        <DemoTour/>
        {/* Container - Full width on tablet/desktop, constrained on mobile */}
        <div className="max-w-md md:max-w-none mx-auto min-h-screen bg-[var(--carte)] md:bg-transparent">
          
          {/* Barre du haut unique « Instagram » (même sur toutes les pages) */}
          {!hideTitle && <IgTopBar title={displayTitle} showBack={showBack} />}

          {/* Main Content */}
          <main ref={swipeRef} className="app-content md:p-6 lg:p-8" data-testid="app-content">
            {/* App Happn (mobile) : le titre vit dans la page, pas dans la barre du haut */}
            {/* Card wrapper for tablet/desktop */}
            <div className="cc-skin md:bg-transparent">
              {/* En-tête expert retiré (bloc « Actions » commun) */}
              <LimiteErreur cle={window.location.pathname}>{children}</LimiteErreur>
              <div id="q35-outils-bas" />
              <AgentHost />
            </div>
          </main>

          {/* Floating Help Button - Hidden on desktop (has sidebar) */}
          <motion.button
            onClick={() => setShowHelpMenu(true)}
            className="hidden"
            style={{ bottom: 'calc(72px + env(safe-area-inset-bottom))' }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            data-testid="floating-help-btn"
          >
            <HelpCircle size={24} />
          </motion.button>

          {/* Help Menu Modal */}
          <AnimatePresence>
            {showHelpMenu && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 z-50"
                  onClick={() => setShowHelpMenu(false)}
                />
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 25 }}
                  className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--carte)] rounded-t-3xl max-h-[70vh] overflow-hidden md:left-auto md:right-4 md:bottom-4 md:w-96 md:rounded-2xl"
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-bold text-[var(--texte)]">Besoin d'aide ?</h2>
                      <button 
                        onClick={() => setShowHelpMenu(false)}
                        className="p-2 hover:bg-[var(--remplissage)] rounded-[14px]"
                      >
                        <X size={24} className="text-[var(--texte-2)]" />
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      <button
                        onClick={handleResetTour}
                        className="w-full p-4 bg-[var(--brand)] text-[var(--sur-couleur)] rounded-xl flex items-center gap-3"
                      >
                        <RotateCcw size={24} />
                        <div className="text-left flex-1">
                          <p className="font-semibold">Revoir le guide</p>
                          <p className="text-sm text-white/70">Tour de formation interactif</p>
                        </div>
                        <ChevronRight size={20} />
                      </button>
                      
                      <button
                        onClick={() => { setShowHelpMenu(false); navigate(`${basePath}/aide`); }}
                        className="w-full p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-center gap-3"
                      >
                        <BookOpen size={24} className="text-[var(--texte)]" />
                        <div className="text-left flex-1">
                          <p className="font-semibold text-[var(--texte)]">Centre d'aide</p>
                          <p className="text-sm text-[var(--texte)]">Tutoriels et FAQ</p>
                        </div>
                        <ChevronRight size={20} className="text-[var(--texte)]" />
                      </button>
                      
                      <button
                        onClick={() => { setShowHelpMenu(false); navigate(`${basePath}/chat`); }}
                        className="w-full p-4 bg-[var(--brand-soft)] border border-[var(--p-fde3d8)] rounded-xl flex items-center gap-3"
                      >
                        <MessageCircle size={24} className="text-[var(--brand)]" />
                        <div className="text-left flex-1">
                          <p className="font-semibold text-[var(--brand)]">Discuter avec Sabril</p>
                          <p className="text-sm text-[var(--brand)]">Posez vos questions</p>
                        </div>
                        <ChevronRight size={20} className="text-[var(--brand)]" />
                      </button>
                    </div>
                    
                    <p className="text-center text-xs text-[var(--texte-2)] mt-4">
                      {tenantName}
                    </p>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {accesParRole}
          <MissionsPopupHote />
          <MarqueImpression />
          {/* Bottom Navigation - Mobile only */}
          <div className="md:hidden">
            <BottomNav />
          </div>
          <BulleExpert />
        </div>
      </div>
      {ficheId && <EmployeeFiche employeeId={ficheId} onClose={() => setFicheId(null)} />}
    </div>
  );
};

export default DashboardLayout;
